import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DEFAULT_SOLUTION_TEXT, INSTALLED_BY } from '@/lib/quoteCalc';
import { RotateCcw } from 'lucide-react';

const L = ({ children }) => <label className="text-[11px] font-medium text-muted-foreground">{children}</label>;
const Box = ({ title, children, testId }) => (
  <fieldset className="border rounded-lg p-3 space-y-2" data-testid={testId}>
    <legend className="text-xs font-semibold px-1">{title}</legend>
    {children}
  </fieldset>
);

export function SolutionField({ form, set }) {
  return (
    <Box title="Om lösningen" testId="quote-solution-section">
      <Textarea rows={4} value={form.solution_text} onChange={e => set({ solution_text: e.target.value })} data-testid="quote-solution-text" />
      {form.solution_text !== DEFAULT_SOLUTION_TEXT && (
        <button type="button" className="text-[11px] text-blue-600 flex items-center gap-1" onClick={() => set({ solution_text: DEFAULT_SOLUTION_TEXT })} data-testid="quote-solution-reset">
          <RotateCcw className="h-3 w-3" /> Återställ standardtext
        </button>
      )}
    </Box>
  );
}

export function InvoiceFields({ form, set }) {
  return (
    <Box title="Fakturering (valfritt)" testId="quote-invoice-section">
      <div className="grid grid-cols-2 gap-2">
        <div><L>Fakturareferens</L><Input value={form.invoice_reference} onChange={e => set({ invoice_reference: e.target.value })} data-testid="quote-invoice-reference" /></div>
        <div><L>Inköpsordernummer</L><Input value={form.po_number} onChange={e => set({ po_number: e.target.value })} data-testid="quote-po-number" /></div>
      </div>
      <div><L>Fakturaadress</L><Textarea rows={2} value={form.invoice_address} onChange={e => set({ invoice_address: e.target.value })} data-testid="quote-invoice-address" /></div>
      <div><L>Betalningsvillkor</L><Input value={form.payment_terms} onChange={e => set({ payment_terms: e.target.value })} data-testid="quote-payment-terms" /></div>
    </Box>
  );
}

export function RenewalFields({ form, set }) {
  return (
    <Box title="Avtalsförlängning" testId="quote-renewal-section">
      <label className="flex items-center gap-2 text-sm">
        <Switch checked={!!form.auto_renew} onCheckedChange={v => set({ auto_renew: v })} data-testid="quote-auto-renew" />
        Automatisk förlängning
      </label>
      {form.auto_renew && (
        <div className="grid grid-cols-2 gap-2">
          <div><L>Förlängs med (månader)</L><Input type="number" min="1" value={form.renewal_months} onChange={e => set({ renewal_months: e.target.value })} data-testid="quote-renewal-months" /></div>
          <div><L>Uppsägningstid (dagar)</L><Input type="number" min="0" value={form.notice_days} onChange={e => set({ notice_days: e.target.value })} data-testid="quote-notice-days" /></div>
        </div>
      )}
    </Box>
  );
}

export function DeliveryFields({ form, set }) {
  const inc = form.installation_included === null ? 'unset' : form.installation_included ? 'yes' : 'no';
  return (
    <Box title="Leverans och installation" testId="quote-delivery-section">
      <div className="grid grid-cols-2 gap-2">
        <div><L>Beräknad leveranstid</L><Input value={form.delivery_time} placeholder="T.ex. 2–3 veckor efter beställning" onChange={e => set({ delivery_time: e.target.value })} data-testid="quote-delivery-time" /></div>
        <div><L>Installation ingår</L>
          <Select value={inc} onValueChange={v => set({ installation_included: v === 'unset' ? null : v === 'yes', ...(v !== 'yes' ? { installation_cost: 0 } : {}) })}>
            <SelectTrigger data-testid="quote-installation-included"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="unset">Ej angivet</SelectItem>
              <SelectItem value="yes">Ja</SelectItem>
              <SelectItem value="no">Nej</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {inc !== 'no' && <div><L>Installationskostnad (SEK, exkl. moms)</L><Input type="number" min="0" value={form.installation_cost} onChange={e => set({ installation_cost: e.target.value })} data-testid="quote-installation-cost" /></div>}
        <div><L>Installation utförs av</L>
          <Select value={form.installation_by || 'unset'} onValueChange={v => set({ installation_by: v === 'unset' ? '' : v })}>
            <SelectTrigger data-testid="quote-installation-by"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="unset">Ej angivet</SelectItem>
              {Object.entries(INSTALLED_BY).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div><L>Tekniska förutsättningar</L><Textarea rows={2} value={form.technical_requirements} onChange={e => set({ technical_requirements: e.target.value })} data-testid="quote-tech-req" /></div>
      <label className="flex items-center gap-2 text-sm">
        <Switch checked={!!form.wifi_required} onCheckedChange={v => set({ wifi_required: v })} data-testid="quote-wifi-required" />
        Krav på tillgängligt och kompatibelt WiFi-nätverk
      </label>
      {form.wifi_required && <Input value={form.wifi_requirements} placeholder="T.ex. 2,4 GHz, WPA2, ej captive portal" onChange={e => set({ wifi_requirements: e.target.value })} data-testid="quote-wifi-req" />}
      <div><L>Övriga kommentarer</L><Textarea rows={2} value={form.delivery_comments} onChange={e => set({ delivery_comments: e.target.value })} data-testid="quote-delivery-comments" /></div>
    </Box>
  );
}
